import { faDotCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Delete } from '@mui/icons-material';
import { FormControl, IconButton, List, ListItem, RadioGroup, Skeleton, Switch, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import usePoll from '../../../hooks/usePoll';
import { EventPollAnswer, EventPollQuestion } from '../../../interfaces/interfaces';
import { fetchAndSetEventPoll, setMeetingPoll } from '../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../reduxToolkit/typedRedux';
import { getPollsPerMeeting } from '../../../services/services';
import { VyConfirm } from '../VyConfirm';
import VyTooltip from '../VyTooltip';
import PollAdd from './PollAdd';
import { PollStats } from './PollStats';
import { VySwitch } from '../VySwitch';
import { usePermission } from '../../../hooks/usePermission';

interface PollControlProps {
  backgroundColor?: string;
  textColor?: string;
}

const PollControl = ({ backgroundColor, textColor }: PollControlProps) => {
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const user = useSelector((state) => state.common.user);
  const meeting = useSelector(({ video }) => video.meeting);
  const polls = useSelector((state) => (meeting ? state.common.meetingPoll : state.common.eventPoll));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { updatePoll, deletePoll, getPollTextColor, getPollBackgroundColor } = usePoll();
  const { isBoothHost } = usePermission();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
  }, [event, meeting, refresh]);

  async function fetchData() {
    setIsLoading(true);
    try {
      if (meeting) {
        const data = await getPollsPerMeeting(meeting._id);
        dispatch(setMeetingPoll(data));
      } else if (event) {
        dispatch(fetchAndSetEventPoll(event?._id));
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  }

  function handlePollAddSubmit(val: { control: boolean; add: boolean }) {
    setRefresh(!refresh);
  }

  const showPollControl =
    user?.role === 'ORGANIZATION_EXTERNAL_SPEAKER' ||
    (event && user?.organization?._id === event?.organization?._id) ||
    isBoothHost(user, meeting?.booth ? [meeting.booth] : undefined);

  if (!showPollControl) return null;

  if (isLoading)
    return (
      <div className="overflow-y-scroll w-full h-full">
        <div className={`flex flex-col p-1 bottom-0 text-center space-y-4`}>
          <div style={{ margin: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} className={`rounded-lg p-2 bg-gray-500`}>
            <Skeleton animation="wave" height={300} width="100%" />
          </div>
          <div style={{ margin: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} className={`rounded-lg p-2 bg-gray-500`}>
            <Skeleton animation="wave" height={300} width="100%" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="w-full h-full">
      <div className="flex flex-col p-2 w-full">
        <div className="mb-2 w-full">
          <PollAdd
            onSubmit={(val: { control: boolean; add: boolean }) => {
              handlePollAddSubmit(val);
            }}
            type={meeting ? 'meeting' : 'stage'}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        </div>
        {polls !== undefined &&
          polls.map((o: EventPollQuestion) => (
            <div
              style={{
                backgroundColor: getPollBackgroundColor(!o.isActive, backgroundColor),
                color: getPollTextColor(!o.isActive, textColor),
              }}
              className={`flex flex-col justify-center rounded-lg w-full p-2 mb-2`}>
              <FormControl component="fieldset">
                <div className="w-full">
                  <p className="break-normal">{o.content}</p>
                  <PollStats question={o} type="question" isLoadingOptionActive={true} />
                </div>

                <RadioGroup aria-label="gender" className="w-full">
                  <List className="w-full">
                    {[...o.answers]
                      ?.sort((a, b) => (a?.sortIndex ?? 0) - (b?.sortIndex ?? 0))
                      ?.map((e: EventPollAnswer) => (
                        <ListItem className="space-x-4 w-full">
                          <div className="flex flex-col gap-2 w-full">
                            <div className="flex flex-row gap-2 items-center">
                              <FontAwesomeIcon
                                icon={faDotCircle}
                                style={{
                                  marginTop: 1,
                                  paddingTop: 2,
                                  color: getPollTextColor(!o.isActive, textColor),
                                }}
                              />
                              <span className="text-sm break-normal" style={{ fontFamily: event?.template.customFont ?? 'Gilroy' }}>
                                {e.content}
                              </span>
                            </div>
                            <div className="mb-2 w-full">
                              <PollStats
                                question={o}
                                pollAnswer={e}
                                type="answer"
                                isLoadingOptionActive={true}
                                textColor={getPollTextColor(!o.isActive, textColor)}
                              />
                            </div>
                          </div>
                        </ListItem>
                      ))}
                  </List>
                </RadioGroup>
                <div className="flex flex-col">
                  <div className="flex flex-col space-y-2">
                    <label className="flex">
                      <VySwitch name="isActive" onChange={(e: any) => updatePoll(o._id, o.content, e.target.checked, undefined)} defaultChecked={o.isActive} />
                      <VyTooltip title={t('poll:is-active-tooltip')}>
                        <span>{t('poll:is-active')}</span>
                      </VyTooltip>
                    </label>
                    <label className="flex">
                      <VySwitch
                        name="allowMultiSelect"
                        onChange={async (e) => updatePoll(o._id, undefined, undefined, undefined, e.target.checked)}
                        defaultChecked={o.allowMultiSelect}
                      />
                      <VyTooltip title={t('poll:allow-multi-select-tooltip')}>
                        <span>{t('poll:allow-multi-select')}</span>
                      </VyTooltip>
                    </label>
                    <label className="flex">
                      <VySwitch
                        disabled={!o.isActive}
                        name="stats"
                        onChange={async (e) => updatePoll(o._id, undefined, undefined, e.target.checked)}
                        defaultChecked={o.isShared}
                      />
                      <VyTooltip title={t('poll:is-shared-tooltip')}>
                        <span>{t('poll:is-shared')}</span>
                      </VyTooltip>
                    </label>
                  </div>

                  <div className="grid place-items-center w-full text-center">
                    <VyTooltip title={o.isActive ? t('poll:delete-active-poll-info') : t('poll:delete-inactive-poll')}>
                      <VyConfirm onConfirm={() => deletePoll(o._id)} disabled={o.isActive}>
                        <IconButton
                          color="error"
                          disabled={o.isActive}
                          style={{
                            cursor: o.isActive ? 'not-allowed' : 'pointer',
                          }}
                          size="large">
                          <Delete />
                        </IconButton>
                      </VyConfirm>
                    </VyTooltip>
                  </div>
                </div>
              </FormControl>
            </div>
          ))}
      </div>
    </div>
  );
};

export default PollControl;
